import React, { useState } from 'react';
import { ShoppingCart, ChevronDown, ChevronUp, CheckCircle2, PackageCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const isDiscreteUnit = (unit) => {
  if (!unit) return true;
  const u = String(unit).toLowerCase().trim();
  return ['pcs', 'buah', 'botol', 'tutup', 'spray', 'fullset', 'set', 'pack', 'box', 'roll', 'pasang', 'lembar', 'unit', 'dus', 'pouch', 'jar', 'vial', 'tube'].includes(u);
};

const fmtQty = (n, unit = 'pcs') => {
  if (n == null || isNaN(n)) return '—';
  const num = Number(n);
  if (isDiscreteUnit(unit) || Number.isInteger(num)) {
    return new Intl.NumberFormat('id-ID', { maximumFractionDigits: 0 }).format(num);
  }
  return new Intl.NumberFormat('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(num);
};

function NeedBuyItemRow({ comp, onMarkArrived }) {
  const kurang = Math.max(0, (comp.qty_needed || 0) - (comp.stock_available || 0));

  return (
    <div className={`flex items-center gap-3 py-3 px-4 rounded-xl border transition-all ${comp.status === 'pending_client' ? 'bg-amber-50/80 border-amber-200' : 'bg-rose-50/80 border-rose-200'}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-bold text-slate-800">
            {comp.item_name || '—'}
          </p>
          <span className={`inline-flex items-center gap-1 text-[11px] px-2.5 py-0.5 rounded-full border font-bold ${comp.status === 'pending_client' ? 'bg-amber-100 text-amber-800 border-amber-300' : 'bg-rose-100 text-rose-800 border-rose-300'}`}>
            <ShoppingCart className="w-3 h-3" />
            {comp.status === 'pending_client' ? 'Menunggu Klien' : 'Habis di Inventori (Follow-up Purchasing)'}
          </span>
        </div>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          <span className="text-xs text-slate-600">
            Kebutuhan PO: <strong className="text-slate-800">{fmtQty(comp.qty_needed, comp.unit)} {comp.unit || 'pcs'}</strong>
          </span>
          <span className="text-xs text-slate-600">
            · Stok Saat Ini: <strong className="text-rose-600">{fmtQty(comp.stock_available || 0, comp.unit)} {comp.unit || 'pcs'}</strong>
          </span>
          {kurang > 0 && (
            <span className="text-xs text-amber-700 font-bold">· Kurang: {fmtQty(kurang, comp.unit)} {comp.unit || 'pcs'}</span>
          )}
          {comp.vendor && (
            <span className="text-xs text-slate-400">· Vendor: {comp.vendor}</span>
          )}
        </div>
      </div>
      {onMarkArrived && (
        <div className="shrink-0">
          <button
            onClick={() => onMarkArrived(comp)}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-xl border font-bold transition-all bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50 shadow-2xs whitespace-nowrap cursor-pointer"
            title="Tandai item sudah datang / diterima di gudang"
          >
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Tandai Sudah Diterima
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * NeedBuySection — tampilkan daftar item yang habis / kurang di inventori.
 * Item otomatis hilang dari daftar ini ketika stok sudah cukup.
 */
export default function NeedBuySection({ needBuyComps, onMarkArrived }) {
  const [collapsed, setCollapsed] = useState(false);

  if (!needBuyComps || needBuyComps.length === 0) return null;

  const totalItem = needBuyComps.length;
  const needBuyCount = needBuyComps.filter(c => c.status !== 'pending_client').length;
  const pendingClientCount = needBuyComps.filter(c => c.status === 'pending_client').length;

  let titleText = `${totalItem} Bahan Habis / Kurang di Inventori`;
  if (needBuyCount > 0 && pendingClientCount > 0) titleText = `${needBuyCount} Habis di Inventori, ${pendingClientCount} Menunggu Klien`;
  else if (pendingClientCount > 0 && needBuyCount === 0) titleText = `${pendingClientCount} Bahan Menunggu Klien`;

  return (
    <Card className="border-rose-200 bg-rose-50/40 shadow-xs">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-4 h-4 text-rose-600" />
            <CardTitle className="text-base font-bold text-rose-900">
              {titleText}
            </CardTitle>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {needBuyCount > 0 && <Badge className="bg-rose-100 text-rose-800 border-rose-300 font-bold">{needBuyCount} perlu follow-up purchasing</Badge>}
            {pendingClientCount > 0 && <Badge className="bg-amber-100 text-amber-800 border-amber-300 font-bold">{pendingClientCount} menunggu klien</Badge>}
            <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-700 hover:bg-rose-100" onClick={() => setCollapsed(c => !c)}>
              {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            </Button>
          </div>
        </div>
        <p className="text-xs text-rose-700/90 mt-1 font-medium">
          Daftar bahan yang stoknya belum mencukupi di inventori untuk PO ini. Silakan follow-up ke tim Purchasing. Otomatis hilang jika stok sudah tersedia.
        </p>
      </CardHeader>
      {!collapsed && (
        <CardContent className="space-y-2 pt-0">
          {needBuyComps.map((comp, i) => (
            <NeedBuyItemRow
              key={comp.inventory_item_id || i}
              comp={comp}
              onMarkArrived={onMarkArrived}
            />
          ))}
        </CardContent>
      )}
    </Card>
  );
}